import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { CommitSaleRequest } from '../../features/pos/services/commit-sale.service';
import * as data from './mock-data';

interface Route {
  method: string;
  pattern: RegExp;
  body: (match: RegExpMatchArray, req: HttpRequest<unknown>) => unknown;
}

const routes: Route[] = [
  { method: 'POST', pattern: /\/warehouses\/list$/, body: () => data.mockWarehouseList() },
  { method: 'POST', pattern: /\/warehouses\/[^/]+\/stock\/list$/, body: () => data.mockStockList() },
  { method: 'POST', pattern: /\/user-companies\/list$/, body: () => data.mockUserCompanies() },
  { method: 'GET', pattern: /\/companies\/get-basic-data\/([^/?]+)/, body: (m) => data.mockNipData(decodeURIComponent(m[1])) },
  { method: 'GET', pattern: /\/companies\/([^/?]+)$/, body: (m) => data.mockCompanySettings(m[1]) },
  { method: 'PATCH', pattern: /\/companies\/[^/?]+$/, body: () => ({}) },
  { method: 'POST', pattern: /\/clients\/list$/, body: (_m, req) => data.mockClientList(queryOf(req)) },
  { method: 'POST', pattern: /\/clients$/, body: () => data.mockCreateClient() },
  { method: 'POST', pattern: /\/pos\/commit-sale$/, body: (_m, req) => data.mockCommitSale(req.body as CommitSaleRequest) },
  { method: 'POST', pattern: /\/pos\/receipts\/next-number$/, body: () => data.mockNextNumber() },
  { method: 'POST', pattern: /\/pos\/receipts\/[^/]+\/fiscalize$/, body: () => ({}) },
  { method: 'POST', pattern: /\/pos\/receipts$/, body: () => data.mockReceipt() },
  { method: 'GET', pattern: /\/pos\/fiscal-reports$/, body: () => data.mockFiscalReportsList() },
  { method: 'POST', pattern: /\/pos\/fiscal-reports$/, body: (_m, req) => data.mockAddFiscalReport((req.body ?? {}) as Record<string, string>) },
  { method: 'POST', pattern: /\/invoices$/, body: () => data.mockInvoice() },
];

const API_HINTS = ['/warehouses', '/user-companies', '/companies', '/clients', '/pos/', '/invoices'];

@Injectable()
export class MockBackendInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isKeycloak(req.url)) {
      return this.respond(req, req.url.includes('/logout') ? {} : data.mockKeycloakToken());
    }

    if (!this.isApi(req.url)) {
      return next.handle(req);
    }

    for (const route of routes) {
      if (route.method !== req.method) continue;
      const match = req.url.match(route.pattern);
      if (match) {
        return this.respond(req, route.body(match, req));
      }
    }

    return this.respond(req, this.fallback(req));
  }

  private isKeycloak(url: string): boolean {
    return url.includes('/realms/') || url.includes('/protocol/openid-connect/');
  }

  private isApi(url: string): boolean {
    return API_HINTS.some((hint) => url.includes(hint));
  }

  private fallback(req: HttpRequest<unknown>): unknown {
    if (req.method === 'PATCH' || req.method === 'PUT' || req.method === 'DELETE') return {};
    if (req.method === 'POST' && !/\/list$/.test(req.url)) return { id: 'mock-' + Math.random().toString(36).slice(2, 10), ok: true };
    return { data: [], reports: [], count: 0 };
  }

  private respond(req: HttpRequest<unknown>, body: unknown): Observable<HttpEvent<unknown>> {
    return of(new HttpResponse({ status: 200, body, url: req.url })).pipe(delay(120));
  }
}

function queryOf(req: HttpRequest<unknown>): string {
  const body = req.body as { query?: string } | null;
  return body?.query ?? '';
}
